import React, { useState, useEffect, useCallback } from 'react';
import collegeConfig from '../../config/collegeConfig';
import { FaTimes, FaSearch, FaCheckDouble } from 'react-icons/fa';
import StudentHeader from './StudentHeader';
import Pagination, { ITEMS_PER_PAGE } from './Pagination';
import BulkVerifyModal from './BulkVerifyModal';
import Toast from './Toast';
import useToast from './useToast';

const STATUS_LABEL = {
  requested: 'Requested',
  scheduled: 'Scheduled',
  completed: 'Completed',
  cancelled: 'Cancelled'
};

// Shared scoring panel for DOAP and Professionalism — both are
// request -> domain-scored-assessment workflows with the same status enum
// (requested/scheduled/completed/cancelled) and the same assessment
// sub-object shape (just different domain lists, and DOAP alone has a
// globalRating). Not subject-scoped (see ugAetcomRoutes.js's /all comment).
const AssessmentRequestReview = ({
  title,
  apiPath,
  listKey,
  domains,
  hasGlobalRating,
  renderRequestBody,
  ugBatches
}) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('requested');
  const [batchFilter, setBatchFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [scoringEntry, setScoringEntry] = useState(null);
  const [domainScores, setDomainScores] = useState({});
  const [globalRating, setGlobalRating] = useState('');
  const [comments, setComments] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const { toast, showSuccess, showError } = useToast();

  const token = localStorage.getItem('token');
  const authHeaders = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json'
  }), [token]);

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      const params = statusFilter ? `?status=${statusFilter}` : '';
      const res = await fetch(`${collegeConfig.api.baseURL}/ug/${apiPath}/all${params}`, {
        headers: authHeaders()
      });
      const data = await res.json();
      if (data.success) setItems(data[listKey] || []);
    } catch (err) {
      console.error(`Error fetching UG ${apiPath}:`, err);
    } finally {
      setLoading(false);
    }
  }, [authHeaders, apiPath, listKey, statusFilter]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const openScoring = (entry) => {
    setScoringEntry(entry);
    const existing = {};
    (entry.assessment?.domainScores || []).forEach(d => { existing[d.domain] = d.score; });
    setDomainScores(existing);
    setGlobalRating(entry.assessment?.globalRating || '');
    setComments(entry.assessment?.facultyComments || entry.assessment?.facultyRemarks || '');
  };

  const submitAssessment = async () => {
    try {
      setSubmitting(true);
      const payload = {
        domainScores: domains.map(d => ({ domain: d, score: Number(domainScores[d]) || 0 }))
      };
      if (hasGlobalRating) {
        payload.globalRating = Number(globalRating) || 0;
        payload.facultyComments = comments;
      } else {
        payload.facultyRemarks = comments;
      }

      const res = await fetch(`${collegeConfig.api.baseURL}/ug/${apiPath}/${scoringEntry._id}/assess`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (data.success) {
        setScoringEntry(null);
        await fetchItems();
        showSuccess('Assessment submitted successfully');
      } else {
        showError(data.message || 'Failed to submit assessment');
      }
    } catch (err) {
      console.error('Error submitting assessment:', err);
      showError('Failed to submit assessment');
    } finally {
      setSubmitting(false);
    }
  };

  const searchLower = searchTerm.trim().toLowerCase();
  const filtered = items.filter(item => {
    if (batchFilter) {
      const studentBatchId = item.studentId?.profile?.ugBatchId?._id || item.studentId?.profile?.ugBatchId;
      if (!studentBatchId || studentBatchId.toString() !== batchFilter) return false;
    }
    if (!searchLower) return true;
    const name = `${item.studentId?.profile?.firstName || ''} ${item.studentId?.profile?.lastName || ''}`.toLowerCase();
    return name.includes(searchLower);
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, batchFilter, searchTerm]);

  const pageItems = filtered.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  const bulkCandidates = filtered.filter(item => item.status === 'requested');

  return (
    <div className="ugtr-root">
      <Toast toast={toast} />
      <div className="ugtr-header">
        <div>
          <div className="ugtr-header-title">{title}</div>
          <div className="ugtr-header-sub">{filtered.length} entries</div>
        </div>
        <button
          className="ugtr-bulk-btn"
          onClick={() => setShowBulkModal(true)}
          disabled={bulkCandidates.length === 0}
        >
          <FaCheckDouble /> Bulk Verify
        </button>
      </div>

      <div className="ugtr-filters">
        <div className="ugtr-search">
          <FaSearch />
          <input
            type="text"
            placeholder="Search by student name..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="requested">Requested (awaiting assessment)</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
          <option value="">All statuses</option>
        </select>
        <select value={batchFilter} onChange={e => setBatchFilter(e.target.value)}>
          <option value="">All batches</option>
          {(ugBatches || []).map(b => (
            <option key={b._id} value={b._id}>{b.batchLabel}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="ugtr-empty">Loading entries...</div>
      ) : filtered.length === 0 ? (
        <div className="ugtr-empty">No entries match these filters.</div>
      ) : (
        <>
          <div className="ugtr-list">
            {pageItems.map(item => (
              <div key={item._id} className="ugtr-entry-card">
                <StudentHeader
                  student={item.studentId}
                  ugBatches={ugBatches}
                  statusBadge={(
                    <span className={`ugtr-status-badge ugtr-status-${item.status === 'requested' ? 'pending' : item.status === 'completed' ? 'approved' : 'needs_revision'}`}>
                      {STATUS_LABEL[item.status] || item.status}
                    </span>
                  )}
                />

                <div className="ugtr-entry-body">
                  {renderRequestBody(item)}
                  {item.status === 'completed' && item.assessment && (
                    <>
                      {item.assessment.domainScores?.length > 0 && (
                        <div className="ugtr-info-item ugtr-entry-body-full">
                          <span className="ugtr-info-label">Domain Scores</span>
                          <span className="ugtr-info-value">
                            {item.assessment.domainScores.map(d => `${d.domain}: ${d.score}`).join(' · ')}
                          </span>
                        </div>
                      )}
                      {item.assessment.globalRating != null && (
                        <div className="ugtr-info-item">
                          <span className="ugtr-info-label">Global Rating</span>
                          <span className="ugtr-info-value">{item.assessment.globalRating}</span>
                        </div>
                      )}
                      {(item.assessment.facultyComments || item.assessment.facultyRemarks) && (
                        <div className="ugtr-info-item ugtr-entry-body-full">
                          <span className="ugtr-info-label">Faculty {hasGlobalRating ? 'Comments' : 'Remarks'}</span>
                          <span className="ugtr-info-value">{item.assessment.facultyComments || item.assessment.facultyRemarks}</span>
                        </div>
                      )}
                    </>
                  )}
                </div>

                <div className="ugtr-entry-actions">
                  <button className="ugtr-review-btn" onClick={() => openScoring(item)}>
                    {item.status === 'completed' ? 'Edit Assessment' : 'Score Request'}
                  </button>
                </div>
              </div>
            ))}
          </div>
          <Pagination currentPage={currentPage} totalCount={filtered.length} onPageChange={setCurrentPage} />
        </>
      )}

      {scoringEntry && (
        <div className="ugtr-modal-overlay" onClick={() => !submitting && setScoringEntry(null)}>
          <div className="ugtr-modal" onClick={e => e.stopPropagation()}>
            <div className="ugtr-modal-head">
              <h3>Score request</h3>
              <FaTimes onClick={() => !submitting && setScoringEntry(null)} />
            </div>

            {domains.map(domain => (
              <div key={domain} style={{ marginBottom: 10 }}>
                <label className="ugtr-form-label">{domain} (1-5)</label>
                <input
                  type="number"
                  min="1"
                  max="5"
                  className="ugtr-form-textarea"
                  style={{ minHeight: 'auto', padding: '8px 12px' }}
                  value={domainScores[domain] || ''}
                  onChange={e => setDomainScores({ ...domainScores, [domain]: e.target.value })}
                />
              </div>
            ))}

            {hasGlobalRating && (
              <div style={{ marginBottom: 10 }}>
                <label className="ugtr-form-label">Global Rating (1-5)</label>
                <input
                  type="number"
                  min="1"
                  max="5"
                  className="ugtr-form-textarea"
                  style={{ minHeight: 'auto', padding: '8px 12px' }}
                  value={globalRating}
                  onChange={e => setGlobalRating(e.target.value)}
                />
              </div>
            )}

            <label className="ugtr-form-label">{hasGlobalRating ? 'Faculty comments' : 'Faculty remarks'}</label>
            <textarea
              className="ugtr-form-textarea"
              value={comments}
              onChange={e => setComments(e.target.value)}
              placeholder="Notes for the student..."
            />

            <div className="ugtr-modal-actions">
              <button className="ugtr-btn-secondary" onClick={() => setScoringEntry(null)} disabled={submitting}>Cancel</button>
              <button className="ugtr-btn-primary" onClick={submitAssessment} disabled={submitting}>
                {submitting ? 'Submitting...' : 'Submit assessment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showBulkModal && (
        <BulkVerifyModal
          title={`Bulk ${title}`}
          items={bulkCandidates}
          getId={item => item._id}
          renderItemLabel={item => (
            <>
              <strong>{item.studentId?.profile?.firstName} {item.studentId?.profile?.lastName}</strong>
              <span className="ugtr-bulk-item-meta">Requested</span>
            </>
          )}
          renderForm={(formState, setFormState) => (
            <>
              <p className="ugtr-modal-competency">
                These scores and comments are applied identically to every selected request.
              </p>
              {domains.map(domain => (
                <div key={domain} style={{ marginBottom: 10 }}>
                  <label className="ugtr-form-label">{domain} (1-5)</label>
                  <input
                    type="number"
                    min="1"
                    max="5"
                    className="ugtr-form-textarea"
                    style={{ minHeight: 'auto', padding: '8px 12px' }}
                    value={formState.domainScores?.[domain] || ''}
                    onChange={e => setFormState({
                      ...formState,
                      domainScores: { ...formState.domainScores, [domain]: e.target.value }
                    })}
                  />
                </div>
              ))}

              {hasGlobalRating && (
                <div style={{ marginBottom: 10 }}>
                  <label className="ugtr-form-label">Global Rating (1-5)</label>
                  <input
                    type="number"
                    min="1"
                    max="5"
                    className="ugtr-form-textarea"
                    style={{ minHeight: 'auto', padding: '8px 12px' }}
                    value={formState.globalRating || ''}
                    onChange={e => setFormState({ ...formState, globalRating: e.target.value })}
                  />
                </div>
              )}

              <label className="ugtr-form-label">{hasGlobalRating ? 'Faculty comments' : 'Faculty remarks'}</label>
              <textarea
                className="ugtr-form-textarea"
                value={formState.comments || ''}
                onChange={e => setFormState({ ...formState, comments: e.target.value })}
                placeholder="Notes for the students..."
              />
            </>
          )}
          isValid={formState => domains.every(d => formState.domainScores?.[d])}
          onConfirm={async (selectedItems, formState) => {
            const payload = {
              domainScores: domains.map(d => ({ domain: d, score: Number(formState.domainScores[d]) || 0 }))
            };
            if (hasGlobalRating) {
              payload.globalRating = Number(formState.globalRating) || 0;
              payload.facultyComments = formState.comments;
            } else {
              payload.facultyRemarks = formState.comments;
            }

            const results = await Promise.allSettled(
              selectedItems.map(item => fetch(`${collegeConfig.api.baseURL}/ug/${apiPath}/${item._id}/assess`, {
                method: 'PUT',
                headers: authHeaders(),
                body: JSON.stringify(payload)
              }))
            );
            const successCount = results.filter(r => r.status === 'fulfilled' && r.value.ok).length;
            setShowBulkModal(false);
            await fetchItems();
            if (successCount === selectedItems.length) {
              showSuccess(`Successfully assessed ${successCount} requests`);
            } else {
              showError(`Assessed ${successCount} of ${selectedItems.length} requests. Some failed — please retry those.`);
            }
          }}
          onClose={() => setShowBulkModal(false)}
        />
      )}
    </div>
  );
};

export default AssessmentRequestReview;
